import { useMemo } from 'react'
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { CONTACTS_PAGE_SIZE } from '@/constants/api'
import { httpClient } from '@/configs/axios.config'
import type { components } from '@/types/api.generated'

// =============================================================================
// Types from generated API
// =============================================================================

type ContactSearchDto = components['schemas']['ContactSearchDto']
type ContactListResponseDto = components['schemas']['ContactListResponseDto']
type ContactResponseDto = components['schemas']['ContactResponseDto']
type CreateContactDto = components['schemas']['CreateContactDto']
type UpdateContactDto = components['schemas']['UpdateContactDto']
type CompanyResponseDto = components['schemas']['CompanyResponseDto']
type FilterDto = components['schemas']['FilterDto']
type SortDto = components['schemas']['SortDto']

// =============================================================================
// Query Keys Factory
// =============================================================================

export const contactsKeys = {
  all: ['contacts'] as const,
  lists: () => [...contactsKeys.all, 'list'] as const,
  list: (params: Omit<ContactSearchDto, 'cursor'>) =>
    [...contactsKeys.lists(), params] as const,
  detail: (id: string) => [...contactsKeys.all, id] as const,
}

// =============================================================================
// API Functions
// =============================================================================

async function searchContacts(
  params: ContactSearchDto
): Promise<ContactListResponseDto> {
  const response = await httpClient.post<ContactListResponseDto>(
    '/contacts/search',
    params
  )
  return response.data
}

async function createContact(
  data: CreateContactDto
): Promise<ContactResponseDto> {
  const response = await httpClient.post<ContactResponseDto>('/contacts', data)
  return response.data
}

async function getContact(id: string): Promise<ContactResponseDto> {
  const response = await httpClient.get<ContactResponseDto>(`/contacts/${id}`)
  return response.data
}

async function updateContact(
  id: string,
  data: UpdateContactDto
): Promise<ContactResponseDto> {
  const response = await httpClient.patch<ContactResponseDto>(
    `/contacts/${id}`,
    data
  )
  return response.data
}

async function deleteContact(id: string, companyId?: string): Promise<void> {
  await httpClient.delete(`/contacts/${id}`, {
    params: companyId ? { companyId } : undefined,
  })
}

function removeContactFromCompanyData(
  companyData: CompanyResponseDto | undefined,
  contactId: string
): CompanyResponseDto | undefined {
  if (!companyData?.contacts?.length) return companyData

  return {
    ...companyData,
    contacts: companyData.contacts.filter(
      (contact) => contact.id !== contactId
    ),
  }
}

/**
 * Applies an immutable update across all pages in the infinite contacts search cache.
 * Used for optimistic UI updates before server confirmation.
 */
function updateInfiniteContactSearchPages(
  data: InfiniteData<ContactListResponseDto> | undefined,
  updater: (contact: ContactResponseDto) => ContactResponseDto | null
): InfiniteData<ContactListResponseDto> | undefined {
  if (!data) return data

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      data: page.data
        .map((contact) => updater(contact))
        .filter((contact): contact is ContactResponseDto => contact !== null),
    })),
  }
}

// =============================================================================
// Query Hooks
// =============================================================================

interface UseContactsSearchParams {
  filters?: FilterDto[]
  sorts?: SortDto[]
  limit?: number
  enabled?: boolean
}

interface UseContactsSearchResult {
  data: ContactResponseDto[]
  isLoading: boolean
  isError: boolean
  isLoadingMore: boolean
  hasMore: boolean
  fetchMore: () => void
  refetch: () => void
}

/**
 * Hook for searching contacts with infinite scroll pagination
 */
export function useContactsSearch({
  filters = [],
  sorts = [],
  limit = CONTACTS_PAGE_SIZE,
  enabled = true,
}: UseContactsSearchParams = {}): UseContactsSearchResult {
  const searchParams: Omit<ContactSearchDto, 'cursor'> = {
    limit,
    filters: filters.length > 0 ? filters : undefined,
    sorts: sorts.length > 0 ? sorts : undefined,
  }

  const query = useInfiniteQuery({
    queryKey: contactsKeys.list(searchParams),
    queryFn: async ({ pageParam }) => {
      const result = await searchContacts({
        ...searchParams,
        cursor: pageParam,
      })
      return result
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore || !lastPage.nextCursor) return undefined
      // nextCursor from API could be string or object, ensure we return string
      const cursor = lastPage.nextCursor
      return typeof cursor === 'string' ? cursor : undefined
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled,
  })

  // Flatten pages into single array
  const data = useMemo(() => {
    return query.data?.pages.flatMap((page) => page.data) ?? []
  }, [query.data?.pages])

  const handleFetchMore = () => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    isLoadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage ?? false,
    fetchMore: handleFetchMore,
    refetch: query.refetch,
  }
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new contact
 */
export function useCreateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateContactDto) => createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.all })
      queryClient.invalidateQueries({ queryKey: ['company'] })
    },
  })
}

export function useContact(contactId: string, enabled = true) {
  return useQuery({
    queryKey: contactsKeys.detail(contactId),
    queryFn: () => getContact(contactId),
    enabled: enabled && Boolean(contactId),
    staleTime: 5 * 60 * 1000,
  })
}

interface UpdateContactParams {
  id: string
  data: UpdateContactDto
}

export function useUpdateContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: UpdateContactParams) => updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.all })
      queryClient.invalidateQueries({ queryKey: ['company'] })
    },
  })
}

interface DeleteContactParams {
  id: string
  companyId?: string
}

export function useDeleteContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, companyId }: DeleteContactParams) =>
      deleteContact(id, companyId),
    onMutate: async ({ id: contactId, companyId }) => {
      await queryClient.cancelQueries({ queryKey: contactsKeys.all })
      await queryClient.cancelQueries({ queryKey: ['company'] })

      const previousContactLists = queryClient.getQueriesData<
        InfiniteData<ContactListResponseDto>
      >({
        queryKey: contactsKeys.lists(),
      })
      const previousContactDetail =
        queryClient.getQueryData<ContactResponseDto>(
          contactsKeys.detail(contactId)
        )
      const previousCompanies = queryClient.getQueriesData<CompanyResponseDto>({
        queryKey: ['company'],
      })

      if (companyId) {
        queryClient.setQueriesData<InfiniteData<ContactListResponseDto>>(
          { queryKey: contactsKeys.lists() },
          (oldData) =>
            updateInfiniteContactSearchPages(oldData, (contact) => {
              if (contact.id !== contactId) return contact

              return {
                ...contact,
                assignments:
                  contact.assignments?.filter(
                    (assignment) => assignment.companyId !== companyId
                  ) ?? [],
              }
            })
        )

        queryClient.setQueryData<ContactResponseDto | undefined>(
          contactsKeys.detail(contactId),
          (oldData) => {
            if (!oldData) return oldData
            return {
              ...oldData,
              assignments:
                oldData.assignments?.filter(
                  (assignment) => assignment.companyId !== companyId
                ) ?? [],
            }
          }
        )

        queryClient.setQueriesData<CompanyResponseDto>(
          { queryKey: ['company', companyId] },
          (oldData) => removeContactFromCompanyData(oldData, contactId)
        )
      } else {
        queryClient.setQueriesData<InfiniteData<ContactListResponseDto>>(
          { queryKey: contactsKeys.lists() },
          (oldData) =>
            updateInfiniteContactSearchPages(oldData, (contact) =>
              contact.id === contactId ? null : contact
            )
        )

        queryClient.removeQueries({
          queryKey: contactsKeys.detail(contactId),
          exact: true,
        })

        queryClient.setQueriesData<CompanyResponseDto>(
          { queryKey: ['company'] },
          (oldData) => removeContactFromCompanyData(oldData, contactId)
        )
      }

      return {
        previousContactLists,
        previousContactDetail,
        previousCompanies,
      }
    },
    onError: (_error, variables, context) => {
      context?.previousContactLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })

      if (context?.previousContactDetail) {
        queryClient.setQueryData(
          contactsKeys.detail(variables.id),
          context.previousContactDetail
        )
      }

      context?.previousCompanies.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactsKeys.all })
      queryClient.invalidateQueries({ queryKey: ['company'] })
    },
  })
}
